import { createFileRoute, Link } from "@tanstack/react-router"
import { TerminalModuleStrip } from "@/components/terminal/TerminalModuleStrip"
import { Layout } from "@/components/site/Layout"
import { Button } from "@/components/ui/button"
import { MacroLensGauge, MacroLensShowcase, PILLAR_LABELS, climateColor } from "@/components/site/MacroLensShowcase"
import { PILLAR_ARM, PILLAR_WEIGHTS, type PillarId } from "@/lib/macrolens"

const SITE = "https://xauproacademy.com"
const PATH = "/macrolens"
const TITLE = "XAU MacroLens™: Gold Macro and Technical Confluence Score | XAU Pro Academy"
const DESC = "XAU MacroLens™ scores gold from -100 to +100 by combining COT, CPI, NFP, US real yields, GLD options, retail sentiment and seasonality with HTF structure, premium/discount and liquidity. Before every gold trade, there is MacroLens."

const FAQ = [
  { q: "What is XAU MacroLens™?", a: "A gold confluence terminal. It reads ten observed and structural pillars and condenses them into one number, the XAU MacroLens™ Score, from -100 (strong bearish confluence) to +100 (strong bullish confluence)." },
  { q: "How is it different from an edge finder or a currency strength meter?", a: "Multi-asset dashboards cover a broad range of markets. MacroLens is built for one market only, XAUUSD. It adds a technical arm based on institutional structure, premium and discount, and liquidity sweeps, and it reports whether macro and structure agree." },
  { q: "Where does the data come from?", a: "CFTC Commitments of Traders, the US Bureau of Labor Statistics for CPI and payrolls, FRED for the 10Y real yield, CBOE delayed GLD options, release consensus when available, a public retail sentiment sample, ten year seasonality, and live gold candles for structure. Every pillar links to its source report." },
  { q: "Is the score a buy or sell signal?", a: "No. It is a context filter. It tells you which direction has institutional support and when conviction should be reduced. Entries still require a liquidity sweep, displacement and structural confirmation." },
  { q: "What does Aligned, Divergent and Mixed mean?", a: "Aligned means the macro arm and the technical arm point the same way with meaningful strength. Divergent means they point in opposite directions, usually a pullback inside a macro trend. Mixed means neither arm is strong enough to lead." },
  { q: "Is the demo on this page live data?", a: "No. The animated gauge on this page cycles through illustrative scenarios scored by the same engine. The live score, pillar details and report drawers are inside the member terminal." },
  { q: "Can I use it for prop firm challenges?", a: "Yes. A reduced score or a divergent state is a clear reason to size down or stand aside, which protects daily and maximum drawdown limits." },
]

export const Route = createFileRoute("/macrolens")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "gold macro score, xauusd confluence tool, gold edge finder, gold fundamental analysis tool, xauusd sentiment score, gold cot cpi nfp dashboard, macro technical alignment gold" },
      { property: "og:title", content: "XAU MacroLens™: Before every gold trade, there is MacroLens" },
      { property: "og:description", content: "One score for gold. Ten pillars of macro and structure, from -100 to +100, with clear alignment states." },
      { property: "og:type", content: "product" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "XAU MacroLens™", applicationCategory: "FinanceApplication", operatingSystem: "Web", url: `${SITE}${PATH}`, description: DESC, publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE }, featureList: ["XAU MacroLens Score from -100 to +100", "CFTC COT positioning", "US CPI and NFP scoring with matched release surprises", "US 10Y real yield (TIPS) momentum", "GLD options positioning proxy", "Retail sentiment contrarian reading", "Ten year seasonality", "HTF market structure", "Premium and discount location", "Liquidity sweep detection", "Macro and technical alignment state"] }) },
      { type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }) },
      { type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: SITE }, { "@type": "ListItem", position: 2, name: "XAU MacroLens", item: `${SITE}${PATH}` }] }) },
    ],
  }),
  component: Page,
})

const SOURCES: Record<PillarId, { note: string; to?: string }> = {
  cot: { note: "Managed money net positioning and weekly change, CFTC.", to: "/gold-cot-report" },
  cpi: { note: "Headline trend and core momentum, BLS. Release-matched consensus surprise when available.", to: "/gold-inflation-cpi" },
  nfp: { note: "Payrolls and unemployment direction, BLS. Release-matched consensus surprise when available.", to: "/gold-nfp-employment" },
  realYield: { note: "US 10Y TIPS yield, FRED. Gold's opportunity cost, read by monthly momentum.", to: "/gold-real-yield" },
  options: { note: "CBOE delayed GLD open interest. Modest positioning proxy, not signed dealer exposure.", to: "/gold-options-sentiment" },
  retail: { note: "Crowd positioning, read as contrarian liquidity.", to: "/gold-retail-sentiment" },
  seasonality: { note: "Ten year average return for the current month.", to: "/gold-seasonality" },
  structure: { note: "D1 and H4 BOS and pullback state." },
  premiumDiscount: { note: "Where price sits inside the active dealing range." },
  liquidity: { note: "SSL swept and reclaimed, or BSL swept and rejected." },
}

const STATES = [
  { score: 72, name: "Bullish alignment", line: "Macro and structure support the upside.", body: "Both arms point higher with strength. Long interest still requires a qualified POI, displacement and structural confirmation." },
  { score: -68, name: "Bearish alignment", line: "The same clarity. The opposite direction.", body: "Both arms point lower with strength. Short interest still requires a qualified premium POI and confirmed rejection." },
  { score: 8, name: "Divergent / balanced", line: "No agreement. No forced trade.", body: "A balanced score can hide opposing arms or weak conviction. Read both arms and wait for confirmation rather than chasing price." },
]

const ROWS = [
  ["Built for", "XAUUSD only", "Dozens of FX pairs", "Any chart"],
  ["Macro inputs", "COT, CPI, NFP, sentiment, seasonality", "Generic template", "None"],
  ["Technical arm", "HTF structure, premium/discount, liquidity", "Rarely", "Price only"],
  ["Alignment state", "Aligned, Divergent, Mixed", "No", "No"],
  ["Source transparency", "Every pillar linked to its report", "Partial", "n/a"],
  ["Tells you when not to trade", "Yes", "Rarely", "No"],
]

function Page() {
  const pillars = Object.keys(PILLAR_WEIGHTS) as PillarId[]
  return (
    <Layout>
      <TerminalModuleStrip module="XAU MacroLens™" />
      <section className="relative overflow-hidden border-b border-border" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container mx-auto grid gap-10 px-4 py-14 lg:grid-cols-2 lg:items-center lg:py-20">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">XAU MacroLens™</div>
            <h1 className="mt-4 text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
              Before every gold trade, <span className="text-gradient-gold">there is MacroLens.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">
              Gold does not move on one chart. It moves when inflation, positioning, the crowd and structure line up. MacroLens reads all of them and gives you one number: the XAU MacroLens™ Score.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
              <li><span className="text-foreground">One score.</span> From -100 to +100, recalculated as the data changes.</li>
              <li><span className="text-foreground">Ten pillars.</span> Seven macro, three institutional technical.</li>
              <li><span className="text-foreground">One question answered.</span> Do macro and structure agree right now?</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg"><Link to="/auth">Open MacroLens</Link></Button>
              <Button asChild size="lg" variant="outline"><Link to="/pricing">See membership</Link></Button>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Read the confluence. Not the noise.</p>
          </div>
          <MacroLensShowcase />
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-semibold tracking-tight">The trade you lose is usually decided before you enter.</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {[
            ["The perfect setup against the macro", "A clean order block fails because CPI just repriced the dollar. The chart was right. The context was wrong."],
            ["Five tabs, no answer", "COT on one site, CPI on another, sentiment on a third. By the time you combine them, London has already swept the high."],
            ["Conviction without evidence", "Bias formed from a headline or a feeling. Position size follows the feeling, and drawdown follows the size."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-xl border border-border bg-card/50 p-6">
              <h3 className="font-medium">{t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-muted-foreground">MacroLens replaces the guesswork with a pre-trade check that takes thirty seconds. Look at the score, read the alignment state, then decide whether your setup deserves full risk, reduced risk or no risk at all.</p>
      </section>

      <section className="border-y border-border bg-card/30">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-semibold tracking-tight">Ten pillars. One reading.</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">Each pillar is scored from -100 to +100 with fixed, published weights. Missing data lowers coverage instead of being guessed.</p>
          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            {(["macro", "technical"] as const).map((arm) => (
              <div key={arm}>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">{arm === "macro" ? "Macro arm · 30%" : "Technical arm · 70%"}</div>
                <div className="mt-4 space-y-3">
                  {pillars.filter((p) => PILLAR_ARM[p] === arm).map((p) => {
                    const src = SOURCES[p]
                    return (
                      <div key={p} className="rounded-lg border border-border bg-background/50 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium">{PILLAR_LABELS[p]}</span>
                          <span className="font-mono text-xs text-muted-foreground">{PILLAR_WEIGHTS[p]}%</span>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{src.note}</p>
                        {src.to && <Link to={src.to} className="mt-2 inline-block text-xs text-primary underline underline-offset-4">Free report</Link>}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-semibold tracking-tight">When macro and structure agree, gold listens.</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">The score tells you direction and strength. The alignment state tells you whether to act.</p>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {STATES.map((s) => {
            const c = climateColor(s.score).color
            return (
              <div key={s.name} className="relative overflow-hidden rounded-xl border border-border bg-card/50 p-6">
                <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 0%, color-mix(in oklab, ${c} 14%, transparent), transparent 70%)` }} />
                <div className="relative flex flex-col items-center text-center">
                  <MacroLensGauge score={s.score} compact />
                  <div className="font-mono text-xs uppercase tracking-widest" style={{ color: c }}>{s.name}</div>
                  <h3 className="mt-2 font-medium">{s.line}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <section className="border-y border-border bg-card/30">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-semibold tracking-tight">The MacroLens ritual</h2>
          <ol className="mt-8 grid gap-6 md:grid-cols-4">
            {[
              ["Check the score", "Direction and strength of institutional context."],
              ["Read the state", "Aligned, Divergent or Mixed."],
              ["Open the pillar", "See the report behind any reading without leaving the terminal."],
              ["Size accordingly", "Full risk on alignment, reduced on divergence, none when mixed."],
            ].map(([t, b], k) => (
              <li key={t} className="rounded-xl border border-border bg-background/50 p-5">
                <div className="font-mono text-2xl text-primary">0{k + 1}</div>
                <div className="mt-2 font-medium">{t}</div>
                <p className="mt-1 text-sm text-muted-foreground">{b}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-semibold tracking-tight">Built for gold. Not adapted to it.</h2>
        <div className="mt-8 overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-card/60 text-left font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr><th className="p-3"></th><th className="p-3 text-primary">XAU MacroLens™</th><th className="p-3">Multi-Asset Dashboards</th><th className="p-3">Indicators</th></tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r[0]} className="border-t border-border">
                  <td className="p-3 text-muted-foreground">{r[0]}</td>
                  <td className="p-3 text-foreground">{r[1]}</td>
                  <td className="p-3 text-muted-foreground">{r[2]}</td>
                  <td className="p-3 text-muted-foreground">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="border-y border-border bg-card/30">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-semibold tracking-tight">Questions</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {FAQ.map((f) => (
              <details key={f.q} className="rounded-lg border border-border bg-background/50 p-4">
                <summary className="cursor-pointer font-medium">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-20 text-center">
        <h2 className="mx-auto max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl">Stop trading gold through a keyhole.</h2>
        <p className="mx-auto mt-4 max-w-xl text-muted-foreground">See the whole picture before you risk a single dollar. Before every gold trade, there is MacroLens.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg"><Link to="/auth">Open MacroLens</Link></Button>
          <Button asChild size="lg" variant="outline"><Link to="/pricing">See membership</Link></Button>
        </div>
        <p className="mx-auto mt-6 max-w-xl text-xs text-muted-foreground">MacroLens is an analytical context tool, not investment advice. Scores on this page are illustrative. Trading leveraged products carries a high risk of loss.</p>
      </section>
    </Layout>
  )
}
